import { useMemo, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import BankAccountInformation from "./bank-account-information";
import BankingActionButtons from "./banking-action-buttons";
import BankingActivity from "./banking-activity";
import EarnMoreCards from "./earn-more-cards";
import BankingServices from "./banking-services";
import ProfileBar from "../../Organisms/App Utility Bar/profilebar";

import BankAccountInformationSkeleton from "../../Components/Skeleton/bank-account-information-skeleton";
import BankActionButtonsSkeleton from "../../Components/Skeleton/banking-action-buttons-skeleton";
import BankingActivitySkeleton from "../../Components/Skeleton/banking-activity-skeleton";
import EarnMoreCardSkeleton from "../../Components/Skeleton/earn-more-card-skeleton";

import useAstra from "../../CustomHooks/useAstra";
import usePinWheel from "../../CustomHooks/usePinWheel";

import { useDeals, useActivities, useStakeDebit, useConnectedAccounts } from "../../CustomHooks/Queries";

import { isSVDApproved } from "../../Utils/helpers";

import { CircleExclamationOutlined } from "../../Assets/Icons";

import "./banking.scss";
import "./banking-responsive.scss";
import "./banking-skeleton.scss";
import "../Welcome/stake-ai-chat.scss";

function useTypewriter(text = "", speed = 9) {
  const [display, setDisplay] = useState("");
  useEffect(() => {
    setDisplay("");
    let idx = 0;
    let timer = null;
    const tick = () => {
      setDisplay(text.slice(0, idx + 1));
      idx += 1;
      if (idx < text.length) {
        timer = setTimeout(tick, speed);
      }
    };
    timer = setTimeout(tick, speed);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [text, speed]);
  return display;
}

function ChatBubble({ role = "system", children, delayed = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current) ref.current.classList.add("visible");
    }, delayed);
    return () => clearTimeout(t);
  }, [delayed]);
  return (
    <div ref={ref} className={`stake-ai-bubble ${role} fade-in`}>
      {role === "system" && (
        <div className="avatar">
          <img src="/favicon-32x32.png" alt="Stake" />
        </div>
      )}
      <div className="bubble-inner">{children}</div>
    </div>
  );
}

export default function Banking() {
  const location = useLocation();
  const { t } = useTranslation();

  // Only show chat and highlights when fromWelcome=true is in the URL
  // This ensures chat and pulse animations only appear when explicitly coming from welcome flow
  const urlParams = new URLSearchParams(location.search);
  const fromWelcome = urlParams.get("fromWelcome") === "true";

  const { isLoading: isLoadingActivities, data: activitiesData } = useActivities();

  const { isLoading: isLoadingDeals, data: deals } = useDeals();

  const { isLoading: isLoadingConnectedAccounts, data: ConnectedAccounts } = useConnectedAccounts();

  const { isLoading: isLoadingStakeDebit, data: stakeDebit } = useStakeDebit();

  const IsSVDApproved = useMemo(() => isSVDApproved(stakeDebit?.data), [stakeDebit?.data]);

  const { isLoadingPayrollAccounts, payrollAccountsData, ...payrollProps } = usePinWheel(IsSVDApproved);

  const { isInitialisingAstra, isLoadingAstraCards, astraDebitCards } = useAstra();

  const isLoadingBankingActionButtons =
    isLoadingStakeDebit || isLoadingConnectedAccounts || isLoadingAstraCards || isInitialisingAstra;

  const isLoadingEarnMore =
    isLoadingDeals || isLoadingStakeDebit || isLoadingConnectedAccounts || isLoadingPayrollAccounts;

  const isLoadingBankingServices =
    isLoadingStakeDebit ||
    isLoadingConnectedAccounts ||
    isLoadingPayrollAccounts ||
    isLoadingAstraCards ||
    isInitialisingAstra;

  const availableBalance = useMemo(() => {
    return stakeDebit?.data?.data?.available ?? 0;
  }, [stakeDebit]);

  const welcomeMessage = t("banking.welcomeMessage", {
    defaultValue:
      'Welcome to your new Stake account! Now you\'ll earn Cash Back each month you pay rent using Stake. Now, Connect your paycheck by clicking "Express Paycheck" below, or Click on Add Cash to connect an external bank and fund your account now',
  });
  const typedMessage = useTypewriter(fromWelcome ? welcomeMessage : "", 9);
  const [showChat, setShowChat] = useState(true);

  return (
    <div className="banking">
      {fromWelcome && showChat && (
        <div
          className="banking-welcome-chat"
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "360px",
            maxHeight: "500px",
            overflowY: "auto",
            zIndex: 1000,
            background: "#171b26",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
        >
          <button
            onClick={() => setShowChat(false)}
            style={{
              position: "absolute",
              top: "8px",
              left: "12px",
              background: "rgba(23, 27, 38, 0.9)",
              border: "none",
              color: "#f4fdfa",
              cursor: "pointer",
              fontSize: "24px",
              lineHeight: "1",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              transition: "background-color 0.2s",
              zIndex: 10001,
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.15)")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "rgba(23, 27, 38, 0.9)")}
            aria-label="Close chat"
          >
            ×
          </button>
          <ChatBubble role="system" delayed={300}>
            <p className="typewriter">{typedMessage}</p>
          </ChatBubble>
        </div>
      )}
      {/* <div className="page-banner error">
        <CircleExclamationOutlined />
        <div className="message">
          Action required — To continue using your Stake Checking account, you’ll need to migrate to our new banking
          partner. <button className="action">Migrate Now →</button>
        </div>
      </div> */}
      <section>
        <div className="banking-details max-width-container">
          <ProfileBar />
          {isLoadingActivities || isLoadingStakeDebit ? (
            <BankAccountInformationSkeleton />
          ) : (
            <BankAccountInformation
              currentBalance={activitiesData?.data?.data?.currentBalance ?? 0}
              availableBalance={availableBalance}
              stakeDebit={stakeDebit}
            />
          )}
          {isLoadingBankingActionButtons ? (
            <BankActionButtonsSkeleton />
          ) : (
            <BankingActionButtons
              stakeDebit={stakeDebit}
              connectedAccounts={ConnectedAccounts}
              astraDebitCards={astraDebitCards}
              fromWelcome={fromWelcome}
            />
          )}
          {isLoadingActivities ? (
            <BankingActivitySkeleton />
          ) : (
            <BankingActivity bankActivityList={activitiesData ?? []} />
          )}
        </div>
        <div className="earn-more-section max-width-container">
          <div className="section-header">
            <h3 className="section-title">Earn More</h3>
          </div>
          {isLoadingEarnMore ? (
            <EarnMoreCardSkeleton />
          ) : (
            <EarnMoreCards
              connectedAccounts={ConnectedAccounts}
              stakeDebit={stakeDebit}
              deals={deals}
              payrollAccountsData={payrollAccountsData}
              payrollProps={payrollProps}
              fromWelcome={fromWelcome}
            />
          )}
        </div>
        {!isLoadingBankingServices && (
          <div className="banking-services-section max-width-container">
            <div className="section-header">
              <h3 className="section-title">Banking Services</h3>
            </div>
            <BankingServices stakeDebit={stakeDebit} payrollAccountsData={payrollAccountsData} />
          </div>
        )}
      </section>
    </div>
  );
}
